import api from '../api/axios';

export type LeaveType =
  | 'annual_leave'
  | 'sick_leave'
  | 'casual_leave_with_pay'
  | 'comp_off'
  | 'emergency_leave'
  | 'haj'
  | 'loss_of_pay'
  | 'accompanying_leave_first_degree'
  | 'relative_death_immediate'
  | 'relative_death_uncle_aunt'
  | 'maternity'
  | 'paternity'
  | 'unpaid'
  | 'personal'
  | 'vacation'
  | 'sick';

export type LeaveCategory = 'annual' | 'medical' | 'special' | 'bereavement' | 'unpaid' | 'other';

export interface LeaveTypeDefinition {
  value: LeaveType;
  label: string;
  description: string;
  category: LeaveCategory;
  deductsAnnualBalance?: boolean;
}

/** Default annual leave entitlement (Oman labour law baseline) */
export const ANNUAL_LEAVE_ENTITLEMENT_DAYS = 30;

/** Leave types that deduct from annual leave balance */
export const ANNUAL_LEAVE_TYPES = new Set(['annual_leave', 'vacation']);

export const LEAVE_TYPES: LeaveTypeDefinition[] = [
  {
    value: 'annual_leave',
    label: 'Annual Leave',
    description: 'Paid annual leave — 30 days per year after completing one year of service (Oman Labour Law).',
    category: 'annual',
    deductsAnnualBalance: true,
  },
  {
    value: 'sick_leave',
    label: 'Sick Leave',
    description: 'Medical leave for illness or injury. A medical certificate may be required for extended periods.',
    category: 'medical',
  },
  {
    value: 'casual_leave_with_pay',
    label: 'Casual Leave With Pay',
    description: 'Short-notice paid leave for personal matters, subject to manager approval.',
    category: 'other',
  },
  {
    value: 'comp_off',
    label: 'Comp-off',
    description: 'Compensatory time off granted in lieu of overtime or weekend work.',
    category: 'other',
  },
  {
    value: 'emergency_leave',
    label: 'Emergency Leave',
    description: 'Urgent leave for unforeseen personal or family emergencies.',
    category: 'special',
  },
  {
    value: 'haj',
    label: 'HAJ Leave',
    description: 'Leave to perform the Hajj pilgrimage — granted once during employment.',
    category: 'special',
  },
  {
    value: 'loss_of_pay',
    label: 'Loss of Pay',
    description: 'Leave without salary deduction from annual balance — days are unpaid.',
    category: 'unpaid',
  },
  {
    value: 'accompanying_leave_first_degree',
    label: 'Accompanying Leave (First Relative Degree)',
    description: 'Leave to accompany a first-degree relative (parent, child, spouse, sibling) for medical treatment.',
    category: 'special',
  },
  {
    value: 'relative_death_immediate',
    label: 'Bereavement — Immediate Family',
    description: 'Compassionate leave for the death of father, mother, grandparents, or siblings.',
    category: 'bereavement',
  },
  {
    value: 'relative_death_uncle_aunt',
    label: 'Bereavement — Uncle or Aunt',
    description: 'Compassionate leave for the death of an uncle or aunt.',
    category: 'bereavement',
  },
  {
    value: 'maternity',
    label: 'Maternity Leave',
    description: 'Paid maternity leave for female employees — 50 days as per Oman Labour Law.',
    category: 'special',
  },
  {
    value: 'paternity',
    label: 'Paternity Leave',
    description: 'Paid paternity leave for male employees following the birth of a child.',
    category: 'special',
  },
  {
    value: 'unpaid',
    label: 'Unpaid Leave',
    description: 'Extended leave without pay, approved at management discretion.',
    category: 'unpaid',
  },
  {
    value: 'personal',
    label: 'Personal Leave',
    description: 'Personal time off for non-emergency personal matters.',
    category: 'other',
  },
];

export const LEAVE_TYPE_LABELS: Record<string, string> = LEAVE_TYPES.reduce(
  (acc, type) => {
    acc[type.value] = type.label;
    return acc;
  },
  { vacation: 'Annual Leave', sick: 'Sick Leave' } as Record<string, string>
);

export const LEAVE_TYPE_DESCRIPTIONS: Record<string, string> = LEAVE_TYPES.reduce(
  (acc, type) => {
    acc[type.value] = type.description;
    return acc;
  },
  {} as Record<string, string>
);

export const LEAVE_CATEGORY_LABELS: Record<LeaveCategory, string> = {
  annual: 'Annual Leave',
  medical: 'Medical Leave',
  special: 'Special Leave',
  bereavement: 'Bereavement Leave',
  unpaid: 'Unpaid Leave',
  other: 'Other Leave',
};

export interface LeaveRequest {
  id?: number;
  employee: number;
  leaveType: LeaveType;
  startDate: string;
  endDate: string;
  reason?: string;
  status?: 'pending' | 'approved' | 'rejected' | 'cancelled';
  approvedBy?: number;
  approvalDate?: string;
  rejectedBy?: number;
  rejectionDate?: string;
  rejectionReason?: string;
  totalDays?: number;
}

const LeaveRequestService = {
  getAll: (params?: any) => api.get('/leave-requests', { params }),
  getOne: (id: number) => api.get(`/leave-requests/${id}`),
  create: (data: LeaveRequest) => api.post('/leave-requests', { data }),
  update: (id: number, data: Partial<LeaveRequest>) => api.put(`/leave-requests/${id}`, { data }),
  delete: (id: number) => api.delete(`/leave-requests/${id}`),
  approve: (id: number, approvedBy: number | null) =>
    api.put(`/leave-requests/${id}`, {
      data: {
        status: 'approved',
        ...(approvedBy !== null && { approvedBy }),
        approvalDate: new Date().toISOString(),
      },
    }),
  reject: (id: number, rejectionReason: string, rejectedBy: number | null) =>
    api.put(`/leave-requests/${id}`, {
      data: {
        status: 'rejected',
        rejectionReason,
        ...(rejectedBy !== null && { rejectedBy }),
        rejectionDate: new Date().toISOString(),
      },
    }),
};

export default LeaveRequestService;
